/**
 * Text handling for the `client_error` event, shared by the browser reporter
 * (src/lib/errors.js) and the intake's validator (server/events.mjs).
 *
 * An error message is the one free-text field in the whole event schema, so it is scrubbed
 * on both sides: the browser never sends the raw string, and the server scrubs again before
 * the row is written (an older or tampered client can't slip an address through). What is
 * removed is everything that could carry identity: email addresses, query strings and
 * fragments, basic-auth credentials in a URL, and long opaque runs that look like a token,
 * id or hash. Nothing else about the message is changed — the point is to keep
 * "TypeError: x.y is not a function" useful while making "…?email=a@b.c" impossible.
 */

/** Longest message and file we store. */
export const MESSAGE_MAX = 200;
export const FILE_MAX = 200;
/** What a redacted run is replaced with. */
export const REDACTED = "[redacted]";

const EMAIL = /[^\s<>()[\]{}"'`]+@[A-Za-z0-9-]+(?:\.[A-Za-z0-9-]+)+/g;
// A URL's query string or fragment: short-link refs, magic-link tokens, utm values. It has
// to be attached to something (the captured character), so a message that merely contains
// "#1" or a bare "?" keeps it. No lookbehind: it is still missing from older iOS Safari,
// where a SyntaxError here would take the whole bundle down.
const QUERY = /(\S)[?#][^\s]*/g;
// user:pass@host inside a URL.
const USERINFO = /(\b[a-z][a-z0-9+.-]*:\/\/)[^/\s@]+@/gi;
// A long opaque run (token, session id, hash). 24+ chars of id alphabet with no separator.
const TOKEN = /\b[A-Za-z0-9_-]{24,}\b/g;

/**
 * Scrub one piece of error text. Order matters: URL credentials first (so `user:pw@host`
 * loses the credentials and keeps the host, instead of the email rule swallowing the whole
 * URL), then addresses, then query strings, then long opaque runs.
 */
export function redactErrorText(value, max = MESSAGE_MAX) {
  if (typeof value !== "string") return "";
  return value
    .replace(USERINFO, `$1${REDACTED}@`)
    .replace(EMAIL, REDACTED)
    .replace(QUERY, `$1${REDACTED}`)
    .replace(TOKEN, REDACTED)
    .replace(/\s+/g, " ")
    .trim()
    .slice(0, max);
}

/**
 * The script/asset a failure came from: origin + path only, and just the path when the
 * value was already relative. An unparseable value is scrubbed as plain text. Query strings
 * and fragments never survive, because a failed request's URL is where tokens actually turn
 * up.
 */
export function errorFileText(value, max = FILE_MAX) {
  if (typeof value !== "string" || !value) return "";
  // A base that cannot collide with a real origin, so a relative path stays relative.
  const BASE = "https://barumba.invalid";
  try {
    const url = new URL(value, BASE);
    // Only a real document URL is reported. Anything else (javascript:, data:, blob:) says
    // nothing about which file broke and could carry inline source.
    if (!/^https?:$/.test(url.protocol)) return "";
    const path = url.pathname.split("/").map((part) => {
      // Decode before scrubbing so percent-encoded addresses/tokens cannot bypass it.
      let decoded;
      try { decoded = decodeURIComponent(part); } catch { return REDACTED; }
      return redactErrorText(decoded, Math.max(decoded.length, max));
    }).join("/");
    return `${url.origin === BASE ? "" : url.origin}${path}`.slice(0, max);
  } catch {
    return redactErrorText(value, max);
  }
}
